// src/components/targetGame/StepTargetGameBubble.tsx
// Inject Target Game / Build Target Game Config node provides mystery items via artifact.config.itemBank.
// StepTargetGameBubble provides local gameplay, yes/no parsing, hints, and fallback item bank.

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Maximize2, Mic, Minimize2 } from "lucide-react";
import { useDictation } from "../../hooks/useDictation";

import type {
  TargetGameArtifactConfig,
  TargetGameItemConfig,
  TargetGameTraitMap,
} from "../../types/chat";
import { N8N_TARGET_GAME_URL } from "../../config/n8n";

export type StepTargetGameBubbleHandle = {
  executeVoiceCommand: (raw: string) => boolean;
};

type Props = {
  onGameMessage?: (message: string) => void;
  gptLiveActive?: boolean;
  artifact: TargetGameArtifactConfig;
  onAudioResponse?: (
    audioBase64?: string | null,
    mimeType?: string | null,
    audioUrl?: string | null
  ) => void | Promise<void>;
};

type LogEntry = {
  id: string;
  by: "user" | "buddy";
  text: string;
};

type TargetGameJudgeResponse = {
  ok?: boolean;
  action?: string;
  result?: "yes" | "no" | "correct_guess" | "wrong_guess" | "unclear";
  message?: string;
  isCorrectGuess?: boolean;
  isUnclear?: boolean;
  shouldEndGame?: boolean;
  normalizedGuess?: string | null;
  reason?: string | null;

  audioBase64?: string | null;
  mimeType?: string | null;
  audioUrl?: string | null;
};

const initialLoadKeys = new Set<string>();

const FALLBACK_ITEM_BANK: TargetGameItemConfig[] = [
  {
    name: "toothbrush",
    hints: [
      "Try asking: “Is it usually found in the bathroom?”",
      "Try asking: “Is it used for hygiene?”",
      "Try asking: “Is it something people use every day?”",
      "Try asking: “Is it used on teeth?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: true,
      electricity: false,
      everyday: true,
      bathroom: true,
      kitchen: false,
      cleaning: true,
      hygiene: true,
      toy: false,
      wearable: false,
      homeDecor: false,
      kidUse: true,
      adultUse: true,
      soft: false,
      paper: false,
      fabric: false,
      squeezable: false,
      teethCare: true,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: false,
      sleepRest: false,
      holdsLiquid: false,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "shampoo",
    hints: [
      "Try asking: “Is it usually found in the bathroom?”",
      "Try asking: “Is it used in the shower?”",
      "Try asking: “Is it used for hygiene?”",
      "Try asking: “Is it used on hair?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: true,
      electricity: false,
      everyday: true,
      bathroom: true,
      kitchen: false,
      cleaning: true,
      hygiene: true,
      toy: false,
      wearable: false,
      homeDecor: false,
      kidUse: true,
      adultUse: true,
      soft: false,
      paper: false,
      fabric: false,
      squeezable: true,
      teethCare: false,
      hairCare: true,
      schoolOffice: false,
      writing: false,
      bedroom: false,
      sleepRest: false,
      holdsLiquid: false,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "notebook",
    hints: [
      "Try asking: “Is it usually found in a school or office section?”",
      "Try asking: “Is it used for writing?”",
      "Try asking: “Is it made of paper?”",
      "Try asking: “Does it have pages?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: true,
      electricity: false,
      everyday: true,
      bathroom: false,
      kitchen: false,
      cleaning: false,
      hygiene: false,
      toy: false,
      wearable: false,
      homeDecor: false,
      kidUse: true,
      adultUse: true,
      soft: false,
      paper: true,
      fabric: false,
      squeezable: false,
      teethCare: false,
      hairCare: false,
      schoolOffice: true,
      writing: true,
      bedroom: false,
      sleepRest: false,
      holdsLiquid: false,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "pillow",
    hints: [
      "Try asking: “Is it usually found in a bedroom?”",
      "Try asking: “Is it soft?”",
      "Try asking: “Is it used for sleeping or resting?”",
      "Try asking: “Is it made of fabric?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: false,
      electricity: false,
      everyday: true,
      bathroom: false,
      kitchen: false,
      cleaning: false,
      hygiene: false,
      toy: false,
      wearable: false,
      homeDecor: true,
      kidUse: true,
      adultUse: true,
      soft: true,
      paper: false,
      fabric: true,
      squeezable: true,
      teethCare: false,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: true,
      sleepRest: true,
      holdsLiquid: false,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "water bottle",
    hints: [
      "Try asking: “Does it hold liquid?”",
      "Try asking: “Can it hold water?”",
      "Try asking: “Is it something people use every day?”",
      "Try asking: “Is it usually found in the kitchen or sports section?”",
    ],
    traits: {
      edible: false,
      drinkable: true,
      under20: true,
      electricity: false,
      everyday: true,
      bathroom: false,
      kitchen: true,
      cleaning: false,
      hygiene: false,
      toy: false,
      wearable: false,
      homeDecor: false,
      kidUse: true,
      adultUse: true,
      soft: false,
      paper: false,
      fabric: false,
      squeezable: false,
      teethCare: false,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: false,
      sleepRest: false,
      holdsLiquid: true,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "stuffed animal",
    hints: [
      "Try asking: “Is it usually found in the toy section?”",
      "Try asking: “Is it soft?”",
      "Try asking: “Is it something kids often use?”",
      "Try asking: “Is it something someone might hug?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: true,
      electricity: false,
      everyday: false,
      bathroom: false,
      kitchen: false,
      cleaning: false,
      hygiene: false,
      toy: true,
      wearable: false,
      homeDecor: false,
      kidUse: true,
      adultUse: false,
      soft: true,
      paper: false,
      fabric: true,
      squeezable: true,
      teethCare: false,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: true,
      sleepRest: false,
      holdsLiquid: false,
      upperBody: false,
      warmth: false,
    },
  },
  {
    name: "t-shirt",
    hints: [
      "Try asking: “Is it usually found in the clothing section?”",
      "Try asking: “Is it something people wear?”",
      "Try asking: “Is it made of fabric?”",
      "Try asking: “Does it go on your upper body?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: true,
      electricity: false,
      everyday: true,
      bathroom: false,
      kitchen: false,
      cleaning: false,
      hygiene: false,
      toy: false,
      wearable: true,
      homeDecor: false,
      kidUse: true,
      adultUse: true,
      soft: true,
      paper: false,
      fabric: true,
      squeezable: false,
      teethCare: false,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: false,
      sleepRest: false,
      holdsLiquid: false,
      upperBody: true,
      warmth: false,
    },
  },
  {
    name: "throw blanket",
    hints: [
      "Try asking: “Is it usually found in the home section?”",
      "Try asking: “Is it soft?”",
      "Try asking: “Is it used to stay warm?”",
      "Try asking: “Is it often used on a couch or bed?”",
    ],
    traits: {
      edible: false,
      drinkable: false,
      under20: false,
      electricity: false,
      everyday: true,
      bathroom: false,
      kitchen: false,
      cleaning: false,
      hygiene: false,
      toy: false,
      wearable: false,
      homeDecor: true,
      kidUse: true,
      adultUse: true,
      soft: true,
      paper: false,
      fabric: true,
      squeezable: true,
      teethCare: false,
      hairCare: false,
      schoolOffice: false,
      writing: false,
      bedroom: true,
      sleepRest: true,
      holdsLiquid: false,
      upperBody: false,
      warmth: true,
    },
  },
];

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function randomTargetItem(
  items: TargetGameItemConfig[],
  previousName?: string
): TargetGameItemConfig {
  if (items.length === 0) return FALLBACK_ITEM_BANK[0];

  if (items.length === 1) return items[0];

  const pool = previousName
    ? items.filter((item) => item.name !== previousName)
    : items;

  const safePool = pool.length > 0 ? pool : items;

  return randomItem(safePool);
}

function articleFor(name: string): string {
  const cleaned = name.trim().toLowerCase();

  if (!cleaned) return "a";

  // A few simple non-count / phrase cases can stay article-free if you add them later.
  // Current item bank works well with "a".
  return /^[aeiou]/i.test(cleaned) ? "an" : "a";
}

function sanitizeGuess(text: string): string {
  let guess = text.toLowerCase().trim();

  const prefixes = [
    "is it ",
    "is this ",
    "is the item ",
    "my guess is ",
    "i guess ",
    "could it be ",
    "is it a ",
    "is it an ",
    "is it the ",
  ];

  prefixes.forEach((prefix) => {
    if (guess.startsWith(prefix)) {
      guess = guess.slice(prefix.length);
    }
  });

  return guess
    .replace(/[?.!,]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function textHasAny(text: string, phrases: string[]): boolean {
  return phrases.some((phrase) => text.includes(phrase));
}

function detectTargetTrait(question: string): keyof TargetGameTraitMap | null {
  const normalized = question.toLowerCase();

  const checks: Array<{
    trait: keyof TargetGameTraitMap;
    phrases: string[];
  }> = [
      {
        trait: "edible",
        phrases: [
          "eat",
          "food",
          "snack",
          "meal",
          "can you eat",
          "is it edible",
          "consume",
        ],
      },
      {
        trait: "drinkable",
        phrases: [
          "drink",
          "water",
          "beverage",
          "sip",
          "liquid container",
          "hold water",
          "holds water",
          "holds liquid",
        ],
      },
      {
        trait: "under20",
        phrases: [
          "under $20",
          "under 20",
          "less than 20",
          "cheap",
          "inexpensive",
          "affordable",
          "budget",
          "low cost",
          "expensive",
          "over 20",
          "more than 20",
          "price",
        ],
      },
      {
        trait: "electricity",
        phrases: [
          "electric",
          "battery",
          "plug",
          "charge",
          "charger",
          "powered",
          "cord",
          "outlet",
        ],
      },
      {
        trait: "everyday",
        phrases: [
          "every day",
          "everyday",
          "daily",
          "use often",
          "used often",
          "used a lot",
          "regularly",
          "all the time",
        ],
      },
      {
        trait: "bathroom",
        phrases: ["bathroom", "sink", "shower", "toilet", "vanity", "restroom"],
      },
      {
        trait: "kitchen",
        phrases: [
          "kitchen",
          "pantry",
          "fridge",
          "counter",
          "cooking",
          "cupboard",
          "sports section",
        ],
      },
      {
        trait: "cleaning",
        phrases: [
          "clean",
          "cleaning",
          "wash",
          "scrub",
          "wipe",
          "sanitize",
          "soap",
        ],
      },
      {
        trait: "hygiene",
        phrases: [
          "hygiene",
          "body care",
          "personal care",
          "grooming",
          "self care",
          "self-care",
          "toiletry",
        ],
      },
      {
        trait: "toy",
        phrases: ["toy", "play", "kid toy", "for playtime", "for fun", "play with"],
      },
      {
        trait: "wearable",
        phrases: [
          "wear",
          "worn",
          "wearable",
          "clothing",
          "shirt",
          "pants",
          "on your body",
          "put it on",
          "apparel",
        ],
      },
      {
        trait: "homeDecor",
        phrases: [
          "home decor",
          "decor",
          "decorate",
          "decoration",
          "decorative",
          "living room",
          "bedroom decor",
          "display",
          "looks nice in a room",
          "home section",
          "couch",
        ],
      },
      {
        trait: "kidUse",
        phrases: ["kid", "child", "children", "for kids", "for a kid", "for children"],
      },
      {
        trait: "adultUse",
        phrases: ["adult", "grown up", "for adults", "for a grown up"],
      },
      {
        trait: "soft",
        phrases: ["soft", "fluffy", "plush", "cushy", "comfy"],
      },
      {
        trait: "paper",
        phrases: ["paper", "write on it", "write in it", "notebook paper", "pages"],
      },
      {
        trait: "fabric",
        phrases: ["fabric", "cloth", "textile", "made of cloth", "material"],
      },
      {
        trait: "squeezable",
        phrases: ["squeeze", "squishy", "compress", "hug", "squeezable"],
      },
      {
        trait: "teethCare",
        phrases: [
          "teeth",
          "tooth",
          "brush teeth",
          "used on teeth",
          "for teeth",
          "dental",
        ],
      },
      {
        trait: "hairCare",
        phrases: ["hair", "used on hair", "for hair", "wash hair", "shower"],
      },
      {
        trait: "schoolOffice",
        phrases: [
          "school",
          "office",
          "school section",
          "office section",
          "desk",
          "classroom",
        ],
      },
      {
        trait: "writing",
        phrases: ["write", "writing", "write on", "write in", "take notes", "notes"],
      },
      {
        trait: "bedroom",
        phrases: [
          "bedroom",
          "bed room",
          "bed",
          "on a bed",
          "near a bed",
          "where you sleep",
        ],
      },
      {
        trait: "sleepRest",
        phrases: [
          "sleep",
          "sleeping",
          "rest",
          "resting",
          "nap",
          "used for sleep",
          "used to sleep",
          "used for resting",
        ],
      },
      {
        trait: "holdsLiquid",
        phrases: [
          "hold liquid",
          "holds liquid",
          "hold water",
          "holds water",
          "carry water",
          "contains water",
        ],
      },
      {
        trait: "upperBody",
        phrases: [
          "upper body",
          "torso",
          "chest",
          "shirt",
          "goes on your upper body",
        ],
      },
      {
        trait: "warmth",
        phrases: ["warm", "stay warm", "keep warm", "blanket", "cozy"],
      },
    ];

  for (const check of checks) {
    if (textHasAny(normalized, check.phrases)) return check.trait;
  }

  const intentPatterns: Array<{
    trait: keyof TargetGameTraitMap;
    test: RegExp;
  }> = [
      { trait: "wearable", test: /(wear|worn|put on|dress)/ },
      { trait: "toy", test: /(toy|play with|game for kids)/ },
      {
        trait: "homeDecor",
        test: /(decorate|decoration|decorative|home item|home section|couch)/,
      },
      { trait: "cleaning", test: /(clean|cleaner|washing|scrubbing|wiping)/ },
      { trait: "hygiene", test: /(hygiene|grooming|personal care|self-care)/ },
      { trait: "bathroom", test: /(bathroom|shower|sink|restroom)/ },
      { trait: "kitchen", test: /(kitchen|pantry|cooking|dish|counter|sports section)/ },
      { trait: "soft", test: /(soft|plush|fluffy|cozy)/ },
      { trait: "paper", test: /(paper|page|pages|write)/ },
      { trait: "fabric", test: /(fabric|cloth|textile|material)/ },
      { trait: "squeezable", test: /(squeeze|squish|hug)/ },
      { trait: "electricity", test: /(electric|battery|plug|charge|corded)/ },
      { trait: "under20", test: /(cheap|affordable|budget|expensive|price|over 20|under 20)/ },
      { trait: "everyday", test: /(daily|every day|everyday|regularly)/ },
      { trait: "kidUse", test: /(kid|child|children|little kid)/ },
      { trait: "adultUse", test: /(adult|grown up)/ },
      { trait: "drinkable", test: /(drink|water bottle|holds liquid)/ },
      { trait: "edible", test: /(eat|edible|food)/ },
      { trait: "teethCare", test: /(teeth|tooth|dental|brush teeth)/ },
      { trait: "hairCare", test: /(hair|wash hair|shower)/ },
      { trait: "schoolOffice", test: /(school|office|desk|classroom)/ },
      { trait: "writing", test: /(write|writing|notes|pages)/ },
      { trait: "bedroom", test: /(bedroom|bed|where you sleep)/ },
      { trait: "sleepRest", test: /(sleep|sleeping|rest|resting|nap)/ },
      {
        trait: "holdsLiquid",
        test: /(hold water|holds water|hold liquid|holds liquid|carry water)/,
      },
      { trait: "upperBody", test: /(upper body|torso|chest)/ },
      { trait: "warmth", test: /(stay warm|keep warm|warm|cozy)/ },
    ];

  for (const rule of intentPatterns) {
    if (rule.test.test(normalized)) return rule.trait;
  }

  return null;
}

type NormalizedTargetHint = {
  trait: keyof TargetGameTraitMap | null;
  text: string;
};

function normalizeTargetHint(hint: unknown): NormalizedTargetHint | null {
  if (typeof hint === "string") {
    const text = hint.trim();
    if (!text) return null;

    return {
      trait: detectTargetTrait(text),
      text,
    };
  }

  if (hint && typeof hint === "object") {
    const obj = hint as {
      trait?: keyof TargetGameTraitMap | string | null;
      text?: string | null;
    };

    const text = typeof obj.text === "string" ? obj.text.trim() : "";
    if (!text) return null;

    const inferredTrait = detectTargetTrait(text);

    return {
      trait: (obj.trait as keyof TargetGameTraitMap | null) || inferredTrait,
      text,
    };
  }

  return null;
}

function normalizeHintTextForCompare(text: string): string {
  return text
    .toLowerCase()
    .replace(/^hint:\s*/i, "")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

const StepTargetGameBubble = React.forwardRef<
  StepTargetGameBubbleHandle,
  Props
>(({
  artifact,
  onAudioResponse,
  onGameMessage,
  gptLiveActive = false,
}, ref) => {
  const itemBank = useMemo(() => {
    const fromArtifact = artifact.config?.itemBank;
    return Array.isArray(fromArtifact) && fromArtifact.length > 0
      ? fromArtifact
      : FALLBACK_ITEM_BANK;
  }, [artifact.config?.itemBank]);

  const [targetItem, setTargetItem] = useState<TargetGameItemConfig>(() =>
    randomTargetItem(itemBank)
  );

  useEffect(() => {
    setTargetItem((previous) => {
      const stillExists = itemBank.some((item) => item.name === previous.name);

      if (stillExists) return previous;

      return randomTargetItem(itemBank, previous.name);
    });
  }, [itemBank]);

  const [questionCount, setQuestionCount] = useState(0);
  const [hintCount, setHintCount] = useState(0);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const {
    transcript: dictationTranscript,
    isListening: isDictationListening,
    toggle: toggleDictation,
  } = useDictation();

  const dictationModeRef = useRef<number | null>(null);
  const dictationBaseTextRef = useRef("");
  const currentInputModeRef =
    useRef<"typed" | "dictation" | "realtime_voice">("typed");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isQuit, setIsQuit] = useState(false);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [buddyMessage, setBuddyMessageState] = useState(
    "I have a Target item in mind. Ask a yes or no question, or make a guess when you are ready."
  );

  // Publish Target Game's authoritative Buddy response.
  const setBuddyMessage = useCallback(
    (message: string) => {
      setBuddyMessageState(message);
      onGameMessage?.(message);
    },
    [onGameMessage]
  );

  const [log, setLog] = useState<LogEntry[]>([]);

  const storeName = artifact.config?.store || "Target";
  const goal = artifact.config?.goal || "Guess the item";
  const instructions =
    artifact.config?.instructions ||
    "In the Target Game, Buddy secretly chooses an item from Target. Ask yes or no questions until you can identify it.";

  useEffect(() => {
    if (!dictationTranscript) return;
    if (dictationTranscript.sessionId !== dictationModeRef.current) return;

    const dictated = dictationTranscript.text.trim();
    const base = dictationBaseTextRef.current.trim();

    setInput(base && dictated ? `${base} ${dictated}` : dictated || base);
    currentInputModeRef.current = "dictation";
  }, [dictationTranscript]);

  useEffect(() => {
    if (isDictationListening) return;

    dictationModeRef.current = null;
    dictationBaseTextRef.current = "";
  }, [isDictationListening]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);

  const handleToggleDictation = useCallback(() => {
    if (isDictationListening) {
      toggleDictation();
      return;
    }

    dictationBaseTextRef.current = input.trim();

    const sessionId = toggleDictation();
    if (typeof sessionId === "number") {
      dictationModeRef.current = sessionId;
    }
  }, [input, isDictationListening, toggleDictation]);

  const addLogEntry = (by: LogEntry["by"], text: string) => {
    setLog((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        by,
        text,
      },
      ...prev,
    ]);
  };

  const rawOutboundType =
    (artifact.context as any)?.responseType ??
    (artifact.context as any)?.outputType ??
    (artifact.context as any)?.audioType ??
    (artifact.context as any)?.type ??
    (artifact.config as any)?.responseType ??
    (artifact.config as any)?.outputType ??
    (artifact.config as any)?.audioType ??
    (artifact.config as any)?.type ??
    "text";

  const outboundType =
    gptLiveActive
      ? "text"
      : rawOutboundType === "audio"
        ? "audio"
        : "text";

  const outboundVoiceId =
    (artifact.context as any)?.voiceId ??
    (artifact.config as any)?.voiceId ??
    null;

  const playAudioFromResponse = useCallback(
    async (payload: any) => {
      if (gptLiveActive) return;
      if (!payload || typeof payload !== "object") return;

      const audioBase64 =
        typeof payload.audioBase64 === "string" ? payload.audioBase64 : null;

      const mimeType =
        typeof payload.mimeType === "string" ? payload.mimeType : "audio/mpeg";

      const audioUrl =
        typeof payload.audioUrl === "string" ? payload.audioUrl : null;

      if (!audioBase64 && !audioUrl) return;

      await onAudioResponse?.(audioBase64, mimeType, audioUrl);
    },
    [onAudioResponse, gptLiveActive]
  );

  const callTargetGameAction = useCallback(
    async (
      action: "get_state" | "submit_turn",
      extra: Record<string, unknown> = {}
    ) => {
      if (!N8N_TARGET_GAME_URL) {
        console.warn("[TargetGame] Missing N8N_TARGET_GAME_URL; using local fallback.");
        return null;
      }

      console.log("[TargetGame] calling n8n", {
        action,
        url: N8N_TARGET_GAME_URL,
        sessionId: artifact.context?.sessionId ?? null,
        accessCode: artifact.context?.accessCode ?? null,
        targetItem: targetItem?.name,
        questionCount,
        type: outboundType,
        voiceId: outboundVoiceId,
        inputMode:
          action === "submit_turn" ? currentInputModeRef.current : "widget_event",
        extra,
      });

      const response = await fetch(N8N_TARGET_GAME_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          gameType: "target_game",
          sessionId: artifact.context?.sessionId ?? null,
          accessCode: artifact.context?.accessCode ?? null,
          patientId: artifact.context?.patientId ?? null,

          // Preserve normal chat/audio contract for n8n logging + optional TTS.
          type: outboundType,
          responseType: outboundType,
          voiceId: outboundVoiceId,
          inputMode:
            action === "submit_turn" ? currentInputModeRef.current : "widget_event",

          targetItem,
          itemBank: itemBank.map((item) => item.name),
          questionCount,

          ...extra,
        }),
      });

      console.log("[TargetGame] n8n response", {
        action,
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Target Game request failed: ${response.status} ${text}`);
      }

      const rawData = await response.json();
      const data = (Array.isArray(rawData) ? rawData[0] : rawData) as TargetGameJudgeResponse;

      await playAudioFromResponse(data);

      return data;
    },
    [
      artifact.context,
      targetItem,
      itemBank,
      questionCount,
      outboundType,
      outboundVoiceId,
      playAudioFromResponse,
    ]
  );

  const loadTargetGame = useCallback(async () => {
    if (!N8N_TARGET_GAME_URL) {
      return;
    }

    try {
      const data = await callTargetGameAction("get_state");

      const openingMessage =
        typeof data?.message === "string" && data.message.trim().length > 0
          ? data.message.trim()
          : "I have a Target item in mind. Ask a yes or no question, or make a guess when you are ready.";

      setBuddyMessage(openingMessage);
      setStatus(null);
    } catch (error) {
      console.error("[TargetGame] initial load failed; keeping local opening message.", error);
    }
  }, [callTargetGameAction, setBuddyMessage]);

  useEffect(() => {
    if (!N8N_TARGET_GAME_URL) return;

    const accessCode = artifact.context?.accessCode ?? null;
    const sessionId = artifact.context?.sessionId ?? null;

    if (!accessCode) return;

    const loadKey = `${accessCode}:${sessionId ?? "no-session"}:${N8N_TARGET_GAME_URL}:target-game`;

    if (initialLoadKeys.has(loadKey)) {
      return;
    }

    initialLoadKeys.add(loadKey);

    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await loadTargetGame();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [artifact.context?.accessCode, artifact.context?.sessionId, loadTargetGame]);

  const showStatus = (message: string) => {
    setStatus(message);
  };

  const hideStatus = () => {
    setStatus(null);
  };

  const resetGame = () => {
    setIsGameCompleted(false);
    const nextItem = randomTargetItem(itemBank, targetItem.name);
    setTargetItem(nextItem);
    setQuestionCount(0);
    setHintCount(0);
    setInput("");
    setStatus(null);
    setIsRevealed(false);
    setIsQuit(false);
    setShowRevealConfirm(false);
    setLog([]);
    setBuddyMessage(
      "I have a Target item in mind. Ask a yes or no question, or make a guess when you are ready."
    );
    setIsExpanded(false);
  };

  const quitGame = () => {
    if (isQuit || isSubmitting) return;

    if (isDictationListening) {
      toggleDictation();
    }

    const message =
      "Target Game ended. You can choose another activity or start over with a new item.";

    setIsQuit(true);
    setInput("");
    setShowRevealConfirm(false);
    setStatus("Game ended. Press Start over to play again.");
    setBuddyMessage(message);
    addLogEntry("buddy", message);
  };

  const revealItem = () => {
    const revealMessage = `The mystery item was ${articleFor(targetItem.name)} ${targetItem.name}. Nice effort — you practiced asking focused yes/no questions. Press Start over for a new item.`;

    setIsRevealed(true);
    setShowRevealConfirm(false);
    setInput("");
    setBuddyMessage(revealMessage);
    showStatus("Answer revealed. Press Start over for a new item.");
    addLogEntry("buddy", revealMessage);
  };

  const supportivePrompt = () => {
    const askedTraits = new Set<keyof TargetGameTraitMap>();

    for (const entry of log) {
      if (entry.by !== "user") continue;

      const trait = detectTargetTrait(entry.text);
      if (trait) askedTraits.add(trait);
    }

    const shownHintTexts = new Set(
      log
        .filter((entry) => entry.by === "buddy")
        .map((entry) => normalizeHintTextForCompare(entry.text))
    );

    const itemHints = Array.isArray(targetItem.hints)
      ? targetItem.hints
        .map(normalizeTargetHint)
        .filter((hint): hint is NormalizedTargetHint => Boolean(hint))
      : [];

    const generalPrompts: NormalizedTargetHint[] = [
      {
        trait: null,
        text: "Try asking about where it is usually found.",
      },
      {
        trait: "everyday",
        text: "Try asking: “Is it something people use every day?”",
      },
      {
        trait: null,
        text: "Try asking about what it is made of.",
      },
      {
        trait: null,
        text: "Try asking about what people use it for.",
      },
      {
        trait: null,
        text: "Try making a specific guess based on what you know so far.",
      },
    ];

    const canUseHint = (hint: NormalizedTargetHint) => {
      const normalizedText = normalizeHintTextForCompare(hint.text);

      if (shownHintTexts.has(normalizedText)) return false;
      if (shownHintTexts.has(normalizeHintTextForCompare(`Hint: ${hint.text}`))) {
        return false;
      }

      if (hint.trait && askedTraits.has(hint.trait)) return false;

      return true;
    };

    const nextHint =
      itemHints.find(canUseHint) ??
      generalPrompts.find(canUseHint) ??
      {
        trait: null,
        text: "You have asked lots of helpful questions. Try making your best guess.",
      };

    setHintCount((prev) => prev + 1);
    setStatus(null);
    addLogEntry("buddy", `Hint: ${nextHint.text}`);
    setBuddyMessage(nextHint.text);
  };

  const answerTargetQuestion = (
    rawInput: string,
    options?: { logUser?: boolean; skipQuestionCount?: boolean }
  ) => {
    const raw = rawInput.trim();
    const question = raw.toLowerCase();

    if (!raw) {
      showStatus("Ask a yes or no question or type a guess to keep going.");
      return;
    }

    const isOpenEndedQuestion = /^(where|what|who|when|why|how)\b/i.test(raw);

    if (isOpenEndedQuestion) {
      const redirect =
        "Good question idea — let’s turn it into a yes/no question. Try: “Is it usually found in the bathroom?” or “Is it usually found in the kitchen?”";

      setBuddyMessage(redirect);

      if (options?.logUser !== false) {
        addLogEntry("user", raw);
      }

      addLogEntry("buddy", redirect);
      setStatus(null);
      return;
    }

    hideStatus();

    if (options?.logUser !== false) {
      addLogEntry("user", raw);
    }

    const nextCount = questionCount + 1;

    if (options?.skipQuestionCount !== true) {
      setQuestionCount(nextCount);
    }

    const itemName = targetItem.name.toLowerCase();
    const cleanedGuess = sanitizeGuess(raw);
    const fuzzyGuesses = [
      itemName,
      `a ${itemName}`,
      `an ${itemName}`,
      `the ${itemName}`,
    ];

    if (fuzzyGuesses.includes(cleanedGuess)) {
      const winMessage = `Yes!! It is ${articleFor(targetItem.name)} ${targetItem.name
        }. Nice job figuring it out in ${nextCount} question${nextCount === 1 ? "" : "s"
        }.`;

      addLogEntry("buddy", winMessage);
      setBuddyMessage(winMessage);
      setIsGameCompleted(true);
      setIsRevealed(true);
      showStatus(
        "Great work. You used focused, step-by-step questioning to solve it. Press Start over for a new item."
      );
      return;
    }

    const itemNames = itemBank.map((item) => item.name.toLowerCase());

    const isConcreteGuess =
      cleanedGuess.length > 0 &&
      itemNames.some((name) => {
        return (
          cleanedGuess === name ||
          cleanedGuess === `a ${name}` ||
          cleanedGuess === `an ${name}` ||
          cleanedGuess === `the ${name}`
        );
      });

    if (isConcreteGuess) {
      const answer = "No.";
      addLogEntry("buddy", answer);
      setBuddyMessage(answer);
      return;
    }

    const matchedTrait = detectTargetTrait(question);

    if (matchedTrait) {
      if (
        matchedTrait === "under20" &&
        textHasAny(question, ["expensive", "over 20", "more than 20"])
      ) {
        const answer = targetItem.traits.under20 ? "No." : "Yes.";
        addLogEntry("buddy", answer);
        setBuddyMessage(answer);
        return;
      }

      if (
        matchedTrait === "adultUse" &&
        textHasAny(question, ["just for kids", "only for kids"])
      ) {
        const answer = targetItem.traits.adultUse ? "No." : "Yes.";
        addLogEntry("buddy", answer);
        setBuddyMessage(answer);
        return;
      }

      const answer = targetItem.traits[matchedTrait] ? "Yes." : "No.";
      addLogEntry("buddy", answer);
      setBuddyMessage(answer);
      return;
    }

    const fallback =
      "I’m not sure how to answer that one yet. Try asking a yes/no question about where it is found, what it is made of, whether it is soft, whether people wear it, whether it holds water, or whether it is used for hygiene or sleep.";

    addLogEntry("buddy", fallback);
    setBuddyMessage(fallback);
  };

  const submitTurn = async (rawOverride?: string) => {
    const raw =
      typeof rawOverride === "string"
        ? rawOverride.trim()
        : input.trim();

    if (
      !raw ||
      isSubmitting ||
      submittingRef.current ||
      isRevealed ||
      isQuit ||
      isGameCompleted
    ) {
      return;
    }

    submittingRef.current = true;

    if (typeof rawOverride === "string") {
      currentInputModeRef.current = "realtime_voice";
    }

    if (isDictationListening) {
      toggleDictation();
    }

    setInput("");
    setStatus(null);
    setIsSubmitting(true);

    addLogEntry("user", raw);

    const nextCount = questionCount + 1;
    setQuestionCount(nextCount);

    try {
      const data = await callTargetGameAction("submit_turn", {
        userInput: raw,
        questionCount: nextCount,
      });

      if (!data) {
        answerTargetQuestion(raw, {
          logUser: false,
          skipQuestionCount: true,
        });

        return;
      }

      const buddyReply =
        typeof data.message === "string" && data.message.trim().length > 0
          ? data.message.trim()
          : "I’m not sure how to answer that one yet. Try asking a yes/no question.";

      addLogEntry("buddy", buddyReply);
      setBuddyMessage(buddyReply);
      currentInputModeRef.current = "typed";

      const isCorrectGuess =
        data.isCorrectGuess === true ||
        data.result === "correct_guess";

      if (isCorrectGuess) {
        setIsRevealed(true);
      }

      if (data.shouldEndGame || isCorrectGuess) {
        setIsGameCompleted(true);
        showStatus(
          "Great work. You used focused, step-by-step questioning to solve it. Press Start over for a new item."
        );
      }
    } catch (error) {
      console.error("[TargetGame] n8n call failed HARD", {
        url: N8N_TARGET_GAME_URL,
        error,
      });

      console.warn("[TargetGame] n8n failed; using local fallback.", error);

      answerTargetQuestion(raw, {
        logUser: false,
        skipQuestionCount: true,
      });

      return;
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    submitTurn();
  };


  // --------------------------------------------------
  // TARGET GAME: GPT-LIVE VOICE COMMANDS
  // --------------------------------------------------
  useImperativeHandle(ref, () => ({
    executeVoiceCommand: (raw: string): boolean => {
      const transcript = String(raw || "").trim();

      const normalized = transcript
        .toLowerCase()
        .replace(/\[[^\]]*\]/g, " ")
        .replace(/[’]/g, "'")
        .replace(/[^\w\s']/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (!normalized) return false;

      // Do not submit another question while n8n is busy.
      if (submittingRef.current || isSubmitting) {
        return true;
      }

      // Start over also works after a completed or quit game.
      const restartCommand = normalized
        .replace(
          /^(?:okay|ok|alright|all right|please|let's|lets)\s+/,
          ""
        )
        .trim();

      if (
        [
          "start over",
          "restart",
          "restart game",
          "restart the game",
          "new game",
          "start a new game",
          "play again",
        ].includes(restartCommand)
      ) {
        resetGame();
        return true;
      }

      // Release voice ownership after the game ends.
      if (isQuit || isRevealed || isGameCompleted) {
        return false;
      }

      if (
        [
          "quit",
          "quit game",
          "quit the game",
          "exit game",
          "stop game",
          "end game",
          "done playing",
        ].includes(normalized)
      ) {
        quitGame();
        return true;
      }

      if (
        ["hint", "give me a hint", "clue", "help"]
          .includes(normalized)
      ) {
        supportivePrompt();
        return true;
      }

      if (
        ["reveal item", "reveal the item", "show answer"]
          .includes(normalized)
      ) {
        setShowRevealConfirm(true);
        setBuddyMessage(
          "Reveal the mystery item? Say Reveal answer or Keep guessing."
        );
        return true;
      }

      if (
        showRevealConfirm &&
        ["reveal answer", "yes reveal answer", "yes"]
          .includes(normalized)
      ) {
        revealItem();
        return true;
      }

      if (
        showRevealConfirm &&
        ["keep guessing", "no", "no keep guessing"]
          .includes(normalized)
      ) {
        setShowRevealConfirm(false);
        setBuddyMessage(
          "Okay. Ask another yes or no question when you're ready."
        );
        return true;
      }

      // Ordinary spoken question or guess.
      // Submit the transcript directly—not via setInput().
      void submitTurn(transcript);

      return true;
    },
  }));

  const primaryButtonClass =
    "h-11 rounded-2xl bg-indigo-600 px-5 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

  const secondaryButtonClass =
    "rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
  const renderTargetGameCard = (expandedView = false) => (
    <div
      className={
        expandedView
          ? "flex h-full max-h-[900px] w-full max-w-6xl flex-col overflow-y-auto rounded-2xl bg-white shadow-2xl"
          : "mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      }
    >
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-base font-semibold text-slate-900">
              {artifact.title || artifact.label || "Target Game"}
            </div>
            <div className="text-sm text-slate-600">
              Ask yes or no questions to narrow down a mystery item.
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-indigo-100"
              aria-label={expandedView ? "Minimize Target Game" : "Maximize Target Game"}
              title={expandedView ? "Minimize" : "Maximize"}
            >
              {expandedView ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
              {expandedView ? "Minimize" : "Maximize"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
        <div className="min-w-0 border-b border-slate-200 bg-slate-50 p-4 lg:col-span-5 lg:border-b-0 lg:border-r">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-xs text-slate-500">Store</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {storeName}
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-xs text-slate-500">Questions</div>
                <div className="mt-1 text-base font-semibold text-slate-900">
                  {questionCount}
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-xs text-slate-500">Goal</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {goal}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm leading-6 text-slate-700">
              <strong>How it works:</strong> {instructions}
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <div className="text-sm font-medium text-slate-700">
                Question log
              </div>

              <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
                {log.length > 0 ? (
                  log.map((entry) => (
                    <div
                      key={entry.id}
                      className={[
                        "rounded-xl border-l-4 px-3 py-2",
                        entry.by === "user"
                          ? "border-indigo-400 bg-indigo-50/70"
                          : "border-emerald-400 bg-emerald-50/70",
                      ].join(" ")}
                    >
                      <div className="text-xs font-medium text-slate-500">
                        {entry.by === "user" ? "You" : "Buddy"}
                      </div>
                      <div className="text-sm font-semibold leading-5 text-slate-900">
                        {entry.text}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
                    No questions asked yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-0 p-4 lg:col-span-7">
          <div className="space-y-4">
            <div className="rounded-[24px] border border-indigo-100 bg-gradient-to-br from-indigo-50 via-indigo-50/60 to-white p-5 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-[24px] border border-indigo-200 bg-white px-3 text-center text-xl font-bold text-indigo-600 shadow-sm">
                  {isRevealed ? targetItem.name : "?"}
                </div>

                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">
                    {isRevealed
                      ? isGameCompleted
                        ? "You guessed it!"
                        : "Answer revealed"
                      : "Mystery item"}
                  </div>
                  <div className="mt-1 text-base text-slate-500">
                    {isRevealed
                      ? isGameCompleted
                        ? "Great job! You identified the mystery item. Start over to play again."
                        : "The answer has been revealed. You can start over with a new item."
                      : "Ask yes or no questions until you can identify it."}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">
                Buddy says
              </div>
              <div className="mt-2 text-sm leading-6 text-slate-800">
                {buddyMessage}
              </div>
            </div>

            {status ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
                {status}
              </div>
            ) : null}

            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="text-sm font-medium text-slate-700">
                Your yes/no question or guess
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Example: “Is it found in the bathroom?” or “Is it a toothbrush?”
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    currentInputModeRef.current = "typed";
                  }}
                  onKeyDown={handleInputKeyDown}
                  disabled={isSubmitting || isRevealed || isQuit || isGameCompleted}
                  placeholder="Ask a yes/no question or make a guess"
                  className="h-11 min-w-0 flex-1 rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                />

                <button
                  type="button"
                  onClick={handleToggleDictation}
                  disabled={isSubmitting || isRevealed || isQuit || isGameCompleted}
                  aria-label={isDictationListening ? "Stop dictation" : "Start dictation"}
                  title={isDictationListening ? "Stop dictation" : "Dictate question"}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition disabled:cursor-not-allowed disabled:opacity-50 ${isDictationListening
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 text-slate-800 hover:bg-gray-300"
                    }`}
                >
                  <Mic className="h-5 w-5" />
                </button>

                <button
                  type="button"
                  onClick={() => void submitTurn()}
                  disabled={!input.trim() || isSubmitting || isRevealed || isQuit || isGameCompleted}
                  className={primaryButtonClass}
                >
                  {isSubmitting ? "Thinking..." : "Submit"}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {artifact.config?.allowHints !== false ? (
                <button
                  type="button"
                  onClick={supportivePrompt}
                  disabled={isRevealed || isQuit || isGameCompleted}
                  className={secondaryButtonClass}
                >
                  Hint
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => setShowRevealConfirm(true)}
                disabled={isRevealed || showRevealConfirm || isQuit || isGameCompleted}
                className={secondaryButtonClass}
              >
                Reveal item
              </button>

              <button
                type="button"
                onClick={resetGame}
                className={secondaryButtonClass}
              >
                Start over
              </button>

              <button
                type="button"
                onClick={quitGame}
                disabled={isQuit || isSubmitting || isGameCompleted || isRevealed}
                className="rounded-full border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Quit
              </button>
            </div>

            {showRevealConfirm ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                <div className="font-semibold">Reveal the mystery item?</div>
                <div className="mt-1">
                  You can keep guessing, ask for a gentle prompt, or reveal the answer and start over.
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={revealItem}
                    className="h-10 rounded-xl bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-500"
                  >
                    Reveal answer
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowRevealConfirm(false)}
                    className={secondaryButtonClass}
                  >
                    Keep guessing
                  </button>
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs leading-5 text-slate-500">
              Target Game uses Buddy’s judge to answer yes/no questions, with a local fallback if the connection is unavailable.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {renderTargetGameCard(false)}

      {isExpanded && typeof document !== "undefined"
        ? createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 p-3 sm:p-5">
            {renderTargetGameCard(true)}
          </div>,
          document.body
        )
        : null}
    </>
  );
});

StepTargetGameBubble.displayName = "StepTargetGameBubble";

export default StepTargetGameBubble;